// src/components/LicenseManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../config';

/* ---------------- helpers: date, status, csv, pagination ---------------- */
function toLocalInput(dt) {
  return dt ? new Date(dt).toISOString().slice(0, 16) : '';
}
function addMonths(dateLike, months) {
  const d = new Date(dateLike || new Date());
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d;
}
function computeStatus(lic) {
  const now = new Date();
  const validUntil = lic?.validUntilUtc ? new Date(lic.validUntilUtc) : null;
  const used = Number(lic?.creditsUsedSinceStart ?? 0);
  const limit = Number(lic?.creditsPerPeriod ?? lic?.usageLimit ?? 0);

  let expiryRed = false, expiryOrange = false;
  if (validUntil) {
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    const diffDays = Math.floor((validUntil - now) / (1000 * 60 * 60 * 24));
    if (validUntil <= endOfToday) expiryRed = true;
    else if (diffDays <= 5) expiryOrange = true;
  }

  let usageRed = false, usageOrange = false;
  if (limit > 0) {
    const pct = used / limit;
    if (pct >= 1) usageRed = true;
    else if (pct >= 0.9) usageOrange = true;
  }
  return { expiryRed, expiryOrange, usageRed, usageOrange, isExpired: expiryRed };
}
function getRowStyleFromStatus(status) {
  if (status.expiryRed || status.usageRed) return { backgroundColor: '#ffe5e5' };
  if (status.expiryOrange || status.usageOrange) return { backgroundColor: '#fff4e5' };
  return {};
}
function getValidUntilTextStyle(lic) {
  const s = computeStatus(lic);
  if (s.expiryRed) return { color: '#c00', fontWeight: 600 };
  if (s.expiryOrange) return { color: '#b45309', fontWeight: 600 };
  return {};
}
function getCurrentUsageTextStyle(lic) {
  const s = computeStatus(lic);
  if (s.usageRed) return { color: '#c00', fontWeight: 600 };
  if (s.usageOrange) return { color: '#b45309', fontWeight: 600 };
  return {};
}
function downloadCsv({ headers, rows, filename }) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.map(h => esc(h.label)).join(',');
  const body = rows.map(r =>
    headers.map(h => esc(h.get ? h.get(r) : r[h.key])).join(',')
  );
  const csv = [head, ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
function usePageSlice(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
function Pagination({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1, canNext = page < pageCount;
  const bar = { display:'flex', gap:8, alignItems:'center', justifyContent:'space-between', padding:12, borderTop:'1px solid #e5e7eb', background:'#fff' };
  const btn = (enabled) => ({ padding:'6px 10px', border:'1px solid #e5e7eb', background:enabled?'#fff':'#f3f4f6', borderRadius:8, cursor:enabled?'pointer':'not-allowed', fontSize:12 });
  const meter = { fontSize:12, color:'#64748b' };
  const select = { padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:12 };
  return (
    <div style={bar}>
      <span style={meter}>Page {page} / {pageCount} • {total.toLocaleString()} rows</span>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <select value={pageSize} onChange={(e)=>onChange({ page:1, pageSize:Number(e.target.value) })} style={select} title="Rows per page">
          {[10,25,50,100,250,500].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={()=>onChange({ page:1 })} disabled={!canPrev} style={btn(canPrev)} title="First">«</button>
        <button onClick={()=>onChange({ page:page-1 })} disabled={!canPrev} style={btn(canPrev)} title="Prev">‹</button>
        <button onClick={()=>onChange({ page:page+1 })} disabled={!canNext} style={btn(canNext)} title="Next">›</button>
        <button onClick={()=>onChange({ page:pageCount })} disabled={!canNext} style={btn(canNext)} title="Last">»</button>
      </div>
    </div>
  );
}

/* ---------------- component ---------------- */
export default function LicenseManagement() {
  // DATA
  const [licenses, setLicenses] = useState([]);

  // CREATE Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    ownerEmail: '',
    planName: '',
    period: 'Fixed',
    allowedCompanyIds: '',
    allowedProjectIds: '',
    allowedUserEmails: '',
    usageLimit: '',
    adminNotes: '',
    emailUser: false
  });

  // EDIT Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: null,
    isEnabled: false,
    isDeleted: false,
    ownerEmail: '',
    planName: '',
    period: 'Fixed',
    creditsPerPeriod: '',
    createdDateUtc: '',
    validUntilUtc: '',
    allowedCompanyIds: '',
    allowedProjectIds: '',
    allowedUserEmails: '',
    adminNotes: ''
  });

  // EMAIL Modal (send licence)
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ toEmail: '', name: '', licenceKey: '' });
  const [emailSending, setEmailSending] = useState(false);

  // FILTERS
  const [filters, setFilters] = useState({
    showIds: false,
    validUntilFrom: '',
    validUntilTo: '',
    status: 'All',
    planName: 'All',
    period: 'All',
    search: '' // quick search (email, id, notes)
  });

  // SORT + PAGINATION
  const [sortConfig, setSortConfig] = useState({ key: 'createdDateUtc', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /* ---------------- data ---------------- */
  const fetchLicenses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/licences`);
      const list = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res.data?.data) ? res.data.data : (res.data?.items ?? []));
      const normalized = (list || []).map(lic => ({
        ...lic,
        adminNotes: lic.adminNotes || '',
        creditsUsedSinceStart: lic.creditsUsedSinceStart ?? 0
      }));
      setLicenses(normalized);
    } catch (err) {
      console.error('Failed to fetch licences:', err);
      alert('Failed to load licences');
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  // Reset to page 1 when filters/sort change
  useEffect(() => { setPage(1); }, [filters, sortConfig]);

  /* ---------------- create ---------------- */
  const handleCreateInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateFormData((f) => ({...f,[name]: type === 'checkbox' ? checked : value}));
  };

  const handleCreateSubmit = async e => {
    e.preventDefault();
    if (!createFormData.ownerEmail || !createFormData.usageLimit) {
      alert('Owner Email and Usage Limit are required');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/licence`, {
        ownerEmail: createFormData.ownerEmail,
        planName: createFormData.planName || '',
        period: createFormData.period || 'Fixed',
        allowedCompanyIds: createFormData.allowedCompanyIds
          .split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n)),
        allowedProjectIds: createFormData.allowedProjectIds
          .split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n)),
        allowedUserEmails: createFormData.allowedUserEmails
          .split(',').map(s => s.trim()).filter(Boolean),
        usageLimit: parseInt(createFormData.usageLimit, 10),
        adminNotes: createFormData.adminNotes || '',
        emailUser: !!createFormData.emailUser
      }, {
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/plain' }
      });

      setShowCreateModal(false);
      setCreateFormData({
        ownerEmail: '',
        planName: '',
        period: 'Fixed',
        allowedCompanyIds: '',
        allowedProjectIds: '',
        allowedUserEmails: '',
        usageLimit: '',
        adminNotes: '',
        emailUser: false
      });
      await fetchLicenses();
    } catch (err) {
      console.error('Failed to create licence:', err.response?.data || err.message);
      alert('Error creating licence');
    }
  };

  /* ---------------- edit ---------------- */
  const handleEditClick = lic => {
    setEditFormData({
      id: lic.id,
      isEnabled: !!lic.isEnabled,
      isDeleted: !!lic.isDeleted,
      ownerEmail: lic.ownerEmail || '',
      planName: lic.planName || '',
      period: lic.period || 'Fixed',
      creditsPerPeriod: lic.creditsPerPeriod || lic.usageLimit || '',
      createdDateUtc: lic.createdDateUtc ? toLocalInput(lic.createdDateUtc) : '',
      validUntilUtc: lic.validUntilUtc ? toLocalInput(lic.validUntilUtc) : '',
      allowedCompanyIds: Array.isArray(lic.allowedCompanyIds) ? lic.allowedCompanyIds.join(',') : '',
      allowedProjectIds: Array.isArray(lic.allowedProjectIds) ? lic.allowedProjectIds.join(',') : '',
      allowedUserEmails: Array.isArray(lic.allowedUserEmails) ? lic.allowedUserEmails.join(',') : '',
      adminNotes: lic.adminNotes || ''
    });
    setEditModalOpen(true);
  };

  const handleExtendClick = async (licence) => {
    const today = new Date();
    const oneMonthFromToday = addMonths(today, 1);

    try {
      await axios.patch(`${API_BASE_URL}/api/licence`, {
        licenceId: licence.id,
        createdDateUtc: today.toISOString(),
        validUntilUtc: oneMonthFromToday.toISOString(),
        isEnabled: licence.isEnabled,
        isDeleted: licence.isDeleted,
        ownerEmail: licence.ownerEmail,
        planName: licence.planName || '',
        period: licence.period || 'Fixed',
        creditsPerPeriod: licence.creditsPerPeriod ?? licence.usageLimit,
        allowedCompanyIds: licence.allowedCompanyIds,
        allowedProjectIds: licence.allowedProjectIds,
        allowedUserEmails: licence.allowedUserEmails,
        adminNotes: licence.adminNotes || ''
      }, {
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/plain' }
      });

      await fetchLicenses();
    } catch (err) {
      console.error('Failed to extend licence:', err.response?.data || err.message);
      alert('Error extending licence');
    }
  };

  const handleEditInputChange = e => {
    const { name, type, checked, value } = e.target;
    setEditFormData(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async e => {
    e.preventDefault();
    try {
      const body = {
        licenceId: editFormData.id,
        isEnabled: editFormData.isEnabled,
        isDeleted: editFormData.isDeleted,
        ownerEmail: editFormData.ownerEmail,
        planName: editFormData.planName || '',
        period: editFormData.period || 'Fixed',
        creditsPerPeriod: parseInt(editFormData.creditsPerPeriod, 10),
        createdDateUtc: editFormData.createdDateUtc ? new Date(editFormData.createdDateUtc).toISOString() : null,
        validUntilUtc: editFormData.validUntilUtc ? new Date(editFormData.validUntilUtc).toISOString() : null,
        allowedCompanyIds: editFormData.allowedCompanyIds.split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n)),
        allowedProjectIds: editFormData.allowedProjectIds.split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n)),
        allowedUserEmails: editFormData.allowedUserEmails.split(',').map(s => s.trim()).filter(Boolean),
        adminNotes: editFormData.adminNotes || ''
      };

      await axios.patch(`${API_BASE_URL}/api/licence`, body, {
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/plain' }
      });

      setEditModalOpen(false);
      await fetchLicenses();
    } catch (err) {
      console.error('Failed to update licence:', err.response?.data || err.message);
      alert('Error updating licence');
    }
  };

  /* ---------------- email modal ---------------- */
  const openEmailModal = (lic) => {
    setEmailForm({
      toEmail: lic.ownerEmail || '',
      name: (lic.ownerEmail || '').split('@')[0],
      licenceKey: lic.id || lic.licenceKey || ''
    });
    setEmailModalOpen(true);
  };
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(f => ({ ...f, [name]: value }));
  };
  const sendLicenceEmails = async () => {
    const recipients = String(emailForm.toEmail || '').split(',').map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) { alert('Please enter at least one email'); return; }
    if (!emailForm.licenceKey) { alert('Missing licence key'); return; }

    setEmailSending(true);
    try {
      for (const toEmail of recipients) {
        await axios.post(`${API_BASE_URL}/api/mail/sendlicence`, {
          toEmail,
          name: emailForm.name || toEmail.split('@')[0],
          licenceKey: emailForm.licenceKey
        });
      }
      setEmailModalOpen(false);
    } catch (err) {
      console.error('Failed to send licence email:', err.response?.data || err.message);
      alert('Error sending email');
    } finally {
      setEmailSending(false);
    }
  };

  /* ---------------- filters + sort + page ---------------- */
  const planNames = useMemo(() => {
    const s = new Set();
    (licenses || []).forEach(l => { if (l.planName) s.add(l.planName); });
    return ['All', ...Array.from(s).sort((a,b)=>a.localeCompare(b))];
  }, [licenses]);

  const filtered = useMemo(() => {
    return (licenses || []).filter(l => {
      // quick search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const fields = [
          l.ownerEmail, l.id, l.planName, l.period, l.adminNotes
        ].concat(l.allowedUserEmails || []).concat((l.allowedCompanyIds||[]).map(String)).concat((l.allowedProjectIds||[]).map(String));
        const match = fields.some(v => String(v || '').toLowerCase().includes(q));
        if (!match) return false;
      }
      // valid until range
      if (filters.validUntilFrom) {
        const vu = l.validUntilUtc ? new Date(l.validUntilUtc) : null;
        if (!vu || vu < new Date(filters.validUntilFrom)) return false;
      }
      if (filters.validUntilTo) {
        const vu = l.validUntilUtc ? new Date(l.validUntilUtc) : null;
        const end = new Date(filters.validUntilTo); end.setHours(23,59,59,999);
        if (!vu || vu > end) return false;
      }
      // status
      const s = computeStatus(l);
      if (filters.status === 'Active' && !l.isEnabled) return false;
      if (filters.status === 'Inactive' && l.isEnabled) return false;
      if (filters.status === 'Expired' && !s.isExpired) return false;
      if (filters.status === 'EndingSoon' && !(s.expiryOrange && !s.expiryRed)) return false;
      if (filters.status === 'OverLimit' && !s.usageRed) return false;
      if (filters.status === 'NearLimit' && !(s.usageOrange && !s.usageRed)) return false;

      // plan name
      if (filters.planName !== 'All' && (l.planName || '') !== filters.planName) return false;
      // period
      if (filters.period !== 'All' && (l.period || '') !== filters.period) return false;

      return true;
    });
  }, [licenses, filters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, direction } = sortConfig;
    arr.sort((a, b) => {
      let aV = a[key], bV = b[key];
      if (key.toLowerCase().includes('date')) { aV = new Date(aV); bV = new Date(bV); }
      else if (key === 'creditsPerPeriod' || key === 'creditsUsedSinceStart') { aV = +aV; bV = +bV; }
      else { aV = (aV ?? '').toString().toLowerCase(); bV = (bV ?? '').toString().toLowerCase(); }
      if (aV < bV) return direction === 'asc' ? -1 : 1;
      if (aV > bV) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortConfig]);

  const currentPageRows = usePageSlice(sorted, page, pageSize);

  // CSV headers + actions
  const csvHeaders = useMemo(() => ([
    { key: 'id', label: 'ID' },
    { key: 'ownerEmail', label: 'Owner Email' },
    { key: 'planName', label: 'Plan' },
    { key: 'period', label: 'Period' },
    { key: 'creditsPerPeriod', label: 'Usage Limit' },
    { key: 'creditsUsedSinceStart', label: 'Current Usage' },
    { key: 'createdDateUtc', label: 'Created (Local)', get: r => r.createdDateUtc ? new Date(r.createdDateUtc).toLocaleString() : '' },
    { key: 'validUntilUtc', label: 'Valid Until (Local)', get: r => r.validUntilUtc ? new Date(r.validUntilUtc).toLocaleString() : '' },
    { key: 'isEnabled', label: 'Enabled' },
    { key: 'isDeleted', label: 'Deleted' },
    { key: 'allowedCompanyIds', label: 'Allowed Companies', get: r => Array.isArray(r.allowedCompanyIds) ? r.allowedCompanyIds.join('|') : '' },
    { key: 'allowedProjectIds', label: 'Allowed Projects', get: r => Array.isArray(r.allowedProjectIds) ? r.allowedProjectIds.join('|') : '' },
    { key: 'allowedUserEmails', label: 'Allowed Emails', get: r => Array.isArray(r.allowedUserEmails) ? r.allowedUserEmails.join('|') : '' },
    { key: 'adminNotes', label: 'Admin Notes' },
  ]), []);
  const exportCurrent = () =>
    downloadCsv({ headers: csvHeaders, rows: currentPageRows, filename: `licences_page-${page}.csv` });
  const exportAll = () =>
    downloadCsv({ headers: csvHeaders, rows: sorted, filename: `licences_all.csv` });

  /* ---------------- UI ---------------- */
  const th = { background:'#f6f7fb', textAlign:'left', padding:10, fontWeight:700, borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' };
  const td = { padding:10, borderBottom:'1px solid #e5e7eb', verticalAlign:'top' };
  const btn = { padding:'8px 12px', borderRadius:10, fontSize:12, fontWeight:700, cursor:'pointer' };
  const btnGhost = { ...btn, background:'#fff', color:'#0f172a', border:'1px solid #e5e7eb' };
  const btnPrimary = { ...btn, background:'#0b0f19', color:'#fff', border:'1px solid #0b0f19' };

  const headers = [
    { key:'id', label:'Licence No.' },
    { key:'ownerEmail', label:'Owner Email' },
    ...(filters.showIds ? [
      { key:'allowedCompanyIds', label:'Company IDs' },
      { key:'allowedProjectIds', label:'Project IDs' },
      { key:'allowedUserEmails', label:'User Emails' },
    ] : []),
    { key:'planName', label:'Plan Name' },
    { key:'period', label:'Period' },
    { key:'creditsPerPeriod', label:'Usage Limit' },
    { key:'creditsUsedSinceStart', label:'Current Usage' },
    { key:'createdDateUtc', label:'Licence Start Date' },
    { key:'validUntilUtc', label:'Valid Until' },
    { key:'lastUsedUtc', label:'Last Used' },
    { key:'status', label:'Status' },
    { key:'adminNotes', label:'Admin Notes' },
    { key:'actions', label:'Actions' }
  ];

  const handleSort = key =>
    setSortConfig(s => ({ key, direction: s.key === key && s.direction === 'asc' ? 'desc' : 'asc' }));

  return (
    <div style={{ padding:16 }}>
      {/* Title + actions */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <h2 style={{ margin:0 }}>License Management</h2>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button onClick={exportCurrent} style={btnGhost}>Export current page</button>
          <button onClick={exportAll} style={btnPrimary}>Export all (filtered)</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        {/* Quick search */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Search</span>
          <input
            placeholder="Email, ID, notes…"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:220 }}
          />
        </div>

        {/* Show IDs */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Columns</span>
          <label style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input
              type="checkbox"
              checked={filters.showIds}
              onChange={e => setFilters(f => ({ ...f, showIds: e.target.checked }))}
            />
            Show ID & Email columns
          </label>
        </div>

        {/* Valid Until range */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Valid Until — From</span>
          <input
            type="date"
            value={filters.validUntilFrom}
            onChange={e => setFilters(f => ({ ...f, validUntilFrom: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:160 }}
          />
        </div>
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Valid Until — To</span>
          <input
            type="date"
            value={filters.validUntilTo}
            onChange={e => setFilters(f => ({ ...f, validUntilTo: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:160 }}
          />
        </div>

        {/* Status */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Status</span>
          <select
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:160, background:'#fff' }}
          >
            {['All','Active','Inactive','Expired','EndingSoon','OverLimit','NearLimit'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Plan Name */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Plan Name</span>
          <select
            value={filters.planName}
            onChange={e => setFilters(f => ({ ...f, planName: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:160, background:'#fff' }}
          >
            {planNames.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Period */}
        <div style={{ display:'flex', flexDirection:'column' }}>
          <span style={tag}>Period</span>
          <select
            value={filters.period}
            onChange={e => setFilters(f => ({ ...f, period: e.target.value }))}
            style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8, minWidth:140, background:'#fff' }}
          >
            {['All','Fixed','Month','Year'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Actions */}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'end' }}>
          <button onClick={() => setShowCreateModal(true)} style={btnPrimary}>Create License</button>
          <button onClick={fetchLicenses} style={btnGhost}>Refresh</button>
          <button
            onClick={() => setFilters({ showIds:false, validUntilFrom:'', validUntilTo:'', status:'All', planName:'All', period:'All', search:'' })}
            style={btnGhost}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX:'auto', border:'1px solid #e5e7eb', borderRadius:12 }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr>
              {headers.map(h => (
                <th
                  key={h.key}
                  onClick={h.key !== 'actions' ? (() => handleSort(h.key)) : undefined}
                  style={{ ...th, cursor: h.key !== 'actions' ? 'pointer' : 'default' }}
                  title={h.key !== 'actions' ? 'Click to sort' : undefined}
                >
                  {h.label}{sortConfig.key === h.key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : null}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={headers.length} style={{ padding:16, textAlign:'center', color:'#64748b' }}>
                  No licences found.
                </td>
              </tr>
            ) : (
              currentPageRows.map((lic) => {
                const status = computeStatus(lic);
                return (
                  <tr key={lic.id} style={getRowStyleFromStatus(status)}>
                    <td style={td}>{lic.id}</td>
                    <td style={{ ...td, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}>{lic.ownerEmail}</td>

                    {filters.showIds && (
                      <>
                        <td style={{ ...td, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}>
                          {Array.isArray(lic.allowedCompanyIds) ? lic.allowedCompanyIds.join(',') : '—'}
                        </td>
                        <td style={{ ...td, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}>
                          {Array.isArray(lic.allowedProjectIds) ? lic.allowedProjectIds.join(',') : '—'}
                        </td>
                        <td style={{ ...td, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}>
                          {Array.isArray(lic.allowedUserEmails) ? lic.allowedUserEmails.join(',') : '—'}
                        </td>
                      </>
                    )}

                    <td style={td}>{lic.planName || '—'}</td>
                    <td style={td}>{lic.period || '—'}</td>
                    <td style={td}>{lic.creditsPerPeriod ?? lic.usageLimit ?? '—'}</td>
                    <td style={{ ...td, ...getCurrentUsageTextStyle(lic) }}>{lic.creditsUsedSinceStart ?? 0}</td>
                    <td style={td}>{lic.createdDateUtc ? format(new Date(lic.createdDateUtc), 'yyyy-MM-dd HH:mm') : '—'}</td>
                    <td style={{ ...td, ...getValidUntilTextStyle(lic) }}>
                      {lic.validUntilUtc ? format(new Date(lic.validUntilUtc), 'yyyy-MM-dd HH:mm') : '—'}
                    </td>
                    <td style={td}>{lic.lastUsedUtc ? format(new Date(lic.lastUsedUtc), 'yyyy-MM-dd HH:mm') : '—'}</td>
                    <td style={td}>{lic.isEnabled ? 'Active' : 'Inactive'}</td>
                    <td style={{ ...td, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis' }}>{lic.adminNotes || '—'}</td>

                    <td style={td}>
                      <button onClick={() => handleEditClick(lic)} style={{ ...btnGhost, padding:'6px 10px', marginRight:6 }}>Edit</button>
                      <button onClick={() => handleExtendClick(lic)} style={{ ...btnGhost, padding:'6px 10px', marginRight:6 }}>Extend</button>
                      <button onClick={() => openEmailModal(lic)} style={{ ...btnGhost, padding:'6px 10px' }} title="Send licence email">
                        📧 Email
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onChange={({ page: p = page, pageSize: ps = pageSize }) => { setPage(p); setPageSize(ps); }}
        />
      </div>

      {/* Create License Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '2rem', borderRadius: '8px',
            width: '90%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.25rem' }}>Create New License</h3>

            <form onSubmit={handleCreateSubmit}>
              <Field label="Owner Email *">
                <input type="email" name="ownerEmail" value={createFormData.ownerEmail} onChange={handleCreateInputChange} required style={inputStyle} />
              </Field>

              <Field label="Plan Name">
                <input type="text" name="planName" value={createFormData.planName} onChange={handleCreateInputChange} placeholder="e.g. Starter, Pro, Enterprise" style={inputStyle} />
              </Field>

              <Field label="Period">
                <select name="period" value={createFormData.period} onChange={handleCreateInputChange} style={inputStyle}>
                  <option value="Fixed">Fixed</option>
                  <option value="Month">Month</option>
                  <option value="Year">Year</option>
                </select>
              </Field>

              <Field label="Company IDs">
                <input type="text" name="allowedCompanyIds" value={createFormData.allowedCompanyIds} onChange={handleCreateInputChange} placeholder="Comma-separated list" style={inputStyle} />
              </Field>

              <Field label="Project IDs">
                <input type="text" name="allowedProjectIds" value={createFormData.allowedProjectIds} onChange={handleCreateInputChange} placeholder="Comma-separated list" style={inputStyle} />
              </Field>

              <Field label="User Emails">
                <input type="text" name="allowedUserEmails" value={createFormData.allowedUserEmails} onChange={handleCreateInputChange} placeholder="Comma-separated list" style={inputStyle} />
              </Field>

              <Field label="Usage Limit *">
                <input type="number" name="usageLimit" value={createFormData.usageLimit} onChange={handleCreateInputChange} required min="1" style={inputStyle} />
              </Field>

              <Field label="Admin Notes">
                <textarea name="adminNotes" value={createFormData.adminNotes} onChange={handleCreateInputChange} placeholder="Internal notes" style={{ ...inputStyle, minHeight: 80 }} />
              </Field>

              <Toggle name="emailUser" checked={createFormData.emailUser} onChange={handleCreateInputChange} label="Email User" />

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={btnSecondary}>Cancel</button>
                <button type="submit" style={btnPrimary}>Create License</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '2rem', borderRadius: '8px',
            width: '90%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', boxSizing: 'border-box'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.25rem' }}>Edit License #{editFormData.id}</h3>
            <form onSubmit={handleEditSubmit}>
              <Toggle name="isEnabled" checked={editFormData.isEnabled} onChange={handleEditInputChange} label="Enabled" />
              <Toggle name="isDeleted" checked={editFormData.isDeleted} onChange={handleEditInputChange} label="Deleted" />

              <Field label="Licence Start Date">
                <input type="datetime-local" name="createdDateUtc" value={editFormData.createdDateUtc} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Owner Email">
                <input type="email" name="ownerEmail" value={editFormData.ownerEmail} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Plan Name">
                <input type="text" name="planName" value={editFormData.planName} onChange={handleEditInputChange} placeholder="e.g. Starter, Pro, Enterprise" style={inputStyle} />
              </Field>

              <Field label="Period">
                <select name="period" value={editFormData.period} onChange={handleEditInputChange} style={inputStyle}>
                  <option value="Fixed">Fixed</option>
                  <option value="Month">Month</option>
                  <option value="Year">Year</option>
                </select>
              </Field>

              <Field label="Usage Limit">
                <input type="number" name="creditsPerPeriod" value={editFormData.creditsPerPeriod} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Valid Until">
                <input type="datetime-local" name="validUntilUtc" value={editFormData.validUntilUtc} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Company IDs">
                <input type="text" name="allowedCompanyIds" value={editFormData.allowedCompanyIds} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Project IDs">
                <input type="text" name="allowedProjectIds" value={editFormData.allowedProjectIds} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="User Emails">
                <input type="text" name="allowedUserEmails" value={editFormData.allowedUserEmails} onChange={handleEditInputChange} style={inputStyle} />
              </Field>

              <Field label="Admin Notes">
                <textarea name="adminNotes" value={editFormData.adminNotes} onChange={handleEditInputChange} placeholder="Enter internal notes for this license..." style={{ ...inputStyle, minHeight: 80 }} />
              </Field>

              <div style={{ display:'flex', gap:'1rem', justifyContent:'flex-end' }}>
                <button type="button" onClick={() => setEditModalOpen(false)} style={btnSecondary}>Cancel</button>
                <button type="submit" style={btnPrimary}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModalOpen && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
        }}>
          <div style={{
            background:'#fff', padding:'1.5rem', borderRadius:8,
            width:'90%', maxWidth:520
          }}>
            <h3 style={{ marginTop:0, marginBottom:12 }}>Send Licence Email</h3>
            <p style={{ marginTop:0, marginBottom:12, color:'#475569', fontSize:13 }}>
              You can enter multiple emails separated by commas.
            </p>
            <Field label="To Email(s)">
              <input name="toEmail" value={emailForm.toEmail} onChange={handleEmailChange} style={inputStyle} placeholder="user@example.com, other@example.com" />
            </Field>
            <Field label="Name">
              <input name="name" value={emailForm.name} onChange={handleEmailChange} style={inputStyle} placeholder="Recipient name" />
            </Field>
            <Field label="Licence Key">
              <input name="licenceKey" value={emailForm.licenceKey} onChange={handleEmailChange} style={inputStyle} />
            </Field>
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button type="button" onClick={() => setEmailModalOpen(false)} style={btnGhost}>Cancel</button>
              <button type="button" onClick={sendLicenceEmails} disabled={emailSending} style={btnPrimary}>
                {emailSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- tiny UI helpers ---------- */
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};
const btnPrimary = {
  background: '#0078d4',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold'
};
const btnSecondary = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px'
};
const tag = {
  display:'inline-block',
  fontSize:11,
  fontWeight:700,
  color:'#0f172a',
  background:'#f1f5f9',
  border:'1px solid #e2e8f0',
  borderRadius:9999,
  padding:'2px 8px',
  marginBottom:6
};
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function Toggle({ name, checked, onChange, label }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input type="checkbox" name={name} checked={checked} onChange={onChange} />
        {label}
      </label>
    </div>
  );
}
